import { useState } from 'react'
import { ChevronDown, Loader2, AlertCircle, Lock } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { stageStyle, stageShort, stageNameOf, STAGE_DEDUCTED, selectableStages, isMaskedStageId, shownStage } from '../../lib/lab.js'
import { useMe } from '../../lib/me.js'
import { labStageScope } from '../../lib/role.js'

// Stage changer rendered as a styled NATIVE <select> (a badge with the stage's
// colour tint). Native on purpose: the picker sits inside the table's
// overflow-x-auto / overflow-hidden container, where a custom absolute menu would be
// clipped — the browser renders a <select>'s option list un-clipped. It's also what
// the old system's stage dropdown looks like.
//
// Kit status (2026-10-18): still native, as a listed KIT GAP. The kit <Select> is
// portalled now, so clipping is no longer the reason — but its trigger is a full-width
// form field and cannot be drawn as this stage-coloured pill. Move it once the kit
// Select takes a custom trigger.
//
// Changing the value POSTs a single stage change (POST /api/lab/status/:id) — the
// proxy stamps employee_id, Xano sets labstage_id + appends a "Stage Change" log
// entry — then calls onChanged(labId, newStageId). An optimistic local value gives
// instant feedback; the parent patch reconciles it (or an error reverts it).
//
// Used inside a clickable table row AND the drawer, so the wrapper stops click
// propagation — using the select must never also open the row's drawer.
//
// `beforeChange(row, nextStageId)` (optional) runs BEFORE the write and can veto it
// by resolving false — used to demand a maker when a piece is marked finished.
//
// TWO stage rules the picker enforces (see lib/lab.js): "หักยอดทันตแพทย์แล้ว" is
// never offered as an option (only the month-wide หักยอดหมอ payroll action sets
// it), and a row already sitting there renders as a locked badge instead of a
// select — its stage is final.
//
// A THIRD, by role (Ram, 2026-10-18 — lib/lab.js §stage visibility): for normal staff
// LAB รับงาน (3) and LAB ทำงานเสร็จ (4) are not options, and a row sitting on either
// shows as one merged "อยู่ที่ LAB" — a disabled placeholder option, so the select still
// renders its current value but nobody can pick it. Moving such a row on (e.g. to ส่ง LAB
// กลับ) works as normal. The scope is read here rather than passed in, so every caller —
// the /lab board, the drawer, the patient page — gets the same rule without wiring.
export default function StagePicker({ row, stages = [], onChanged, disabled, beforeChange }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(false)
  const [optimistic, setOptimistic] = useState(null)

  const curId = Number(row?.labstage_id) || 0
  const shownId = optimistic ?? curId
  const stageById = Object.fromEntries(stages.map((s) => [Number(s.id), s]))
  const scope = labStageScope(useMe())
  const masked = isMaskedStageId(shownId, scope)
  const cur = shownStage(shownId, stages, scope) || row?.labstage || {}
  const st = stageStyle(cur.color)
  const locked = stageNameOf(row, stageById) === STAGE_DEDUCTED
  const options = selectableStages(stages).filter((s) => !isMaskedStageId(s.id, scope))

  async function onChange(e) {
    const next = Number(e.target.value)
    if (!next || next === curId) return
    // A gate the parent can put in front of a specific move (today: a technician
    // marking a piece LAB ทำงานเสร็จ must first say who made it). Returning false
    // abandons the change and leaves the select showing the old stage.
    if (beforeChange) {
      setBusy(true)
      let ok = false
      try { ok = await beforeChange(row, next) } catch { ok = false }
      setBusy(false)
      if (!ok) return
    }
    setOptimistic(next); setBusy(true); setErr(false)
    try {
      await postJson(`/api/lab/status/${row.id}`, { labstage_id: next })
      onChanged?.(row.id, next)
      setOptimistic(null)
    } catch {
      setErr(true); setOptimistic(null)
    } finally {
      setBusy(false)
    }
  }

  if (locked) {
    return (
      <span
        onClick={(e) => e.stopPropagation()}
        style={st.badge}
        title="หักยอดค่า LAB จากเงินเดือนทันตแพทย์แล้ว — เปลี่ยนสถานะไม่ได้"
        className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium max-w-[200px]"
      >
        <Lock size={11} className="shrink-0" />
        <span className="truncate">{stageShort(cur.name)}</span>
      </span>
    )
  }

  return (
    <div className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <select
        value={shownId || ''}
        onChange={onChange}
        disabled={disabled || busy}
        // The global `select` rule (index.css) draws a chevron via background-image on
        // EVERY dropdown; we render our own chevron/spinner overlay, so suppress the
        // global one here — otherwise the select shows a double down-arrow.
        style={{ ...st.badge, backgroundImage: 'none' }}
        title={cur.name || 'เลือกสถานะ'}
        className="appearance-none rounded-full border pl-2.5 pr-7 py-1 text-[12px] font-medium max-w-[200px] truncate outline-none cursor-pointer disabled:opacity-60 focus:ring-2 focus:ring-brand-100"
      >
        {!curId && <option value="">— เลือกสถานะ —</option>}
        {masked && <option value={shownId} disabled className="text-slate-400 bg-white">{cur.name}</option>}
        {options.map((s) => <option key={s.id} value={s.id} className="text-slate-700 bg-white">{stageShort(s.name)}</option>)}
      </select>
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2">
        {busy ? <Loader2 size={12} className="animate-spin text-slate-500" />
          : err ? <AlertCircle size={12} className="text-rose-500" />
            : <ChevronDown size={12} className="opacity-70" style={{ color: st.badge.color }} />}
      </span>
    </div>
  )
}
